import {
  Box,
  useTheme,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import StatBox from "../../components/StatBox";
import {
  AccountBalanceWalletOutlined,
  CardGiftcardOutlined,
  LockOutlined,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import DataGridCustomToolbar from "../../components/DataGridCustomToolbar";
import axios from "axios";
import SendIcon from "@mui/icons-material/Send";
import { getStatementAction } from "../../redux/actions/walletAction";

const WITHDRAWAL_STATUS_COLORS = {
  pending: "#ffda85",
  approved: "#00c9a7",
  rejected: "#de4437",
  cancelled: "#7a7f9d",
};

const UserWalletStatement = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [rowData, setRowData] = useState([]);
  const [users, setUsers] = useState([]);
  const [dates, setDates] = useState({ fromDate: "", toDate: "" });
  const [isDataFetched, setIsDataFetched] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const userId = pathname.split("/").pop();
  const dispatch = useDispatch();
  let [filterData, setFilterData] = useState([]);
  const [userFullName, setUserFullName] = useState("");
  const [walletBalance, setWalletBalance] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(true);
  const [withdrawalPageSize, setWithdrawalPageSize] = useState(10);

  useEffect(() => {
    const fetchWalletBalance = async () => {
      setWalletLoading(true);
      try {
        const response = await axios.post("/wallet/info", { userId });
        if (response.data.success) {
          setWalletBalance(response.data.data);
          if (response.data.data.fullname) {
            setUserFullName(response.data.data.fullname);
          }
        }
      } catch (error) {
        console.error("Error fetching wallet balance:", error);
      } finally {
        setWalletLoading(false);
      }
    };
    fetchWalletBalance();
  }, [userId]);

  useEffect(() => {
    const fetchWithdrawals = async () => {
      setWithdrawalsLoading(true);
      try {
        const response = await axios.get("/wallet/withdraw/list", {
          params: { userId },
        });
        if (response.data.success) {
          setWithdrawalRequests(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching withdrawal requests:", error);
      } finally {
        setWithdrawalsLoading(false);
      }
    };
    fetchWithdrawals();
  }, [userId]);

  useEffect(() => {
    const fetchWalletHistory = async () => {
      try {
        const response = await dispatch(getStatementAction(userId));
        if (response.success) {
          setRowData(response.statementList);
        } else {
          console.error("Error fetching user wallet history");
        }
      } catch (error) {
        console.error("Error fetching user wallet history:", error);
        toast.error("Error fetching user wallet history");
      }
    };
    fetchWalletHistory();
  }, [userId, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get("/wallet/statement/list", {
          params: { userId },
        });

        if (
          response.data.statementList &&
          response.data.statementList.length > 0
        ) {
          setUsers(response.data.statementList);
          const firstEntryUser = response.data.statementList[0].userId;
          if (firstEntryUser && firstEntryUser.fullname) {
            setUserFullName((prev) => prev || firstEntryUser.fullname);
          }
          setIsDataFetched(true);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [userId]);

  // const handleOpen = () => {
  //   if (!isDataFetched) {
  //     fetchData();
  //   }
  // };

  const handleChange = (event, type) => {
    const value = event.target.value;
    if (value === "all") {
      setDates({ fromDate: "", toDate: "" });
      setFilterData({});
    } else {
      if (type === "fromDate" || type === "toDate") {
        setDates((prevDates) => ({
          ...prevDates,
          [type]: value,
        }));
        setFilterData((prevFilters) => ({
          ...prevFilters,
          [type]: value,
        }));
      } else {
        setFilterData((prevFilters) => ({
          ...prevFilters,
          [type]: value,
        }));
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const params = {
        ...filterData,
        userId,
      };
      if (dates.fromDate) params.fromDate = dates.fromDate;
      if (dates.toDate) params.toDate = dates.toDate;

      const response = await axios.get("/wallet/statement/list", { params });
      setRowData(response.data.statementList);
    } catch (error) {
      console.error(error);
    }
  };

  const columns = [
    {
      field: "transType",
      headerName: "Transfer Type",
      flex: 0.5,
    },
    {
      field: "credit",
      headerName: "Credit",
      flex: 0.5,
      renderCell: (params) =>
        params.value ? `NRs ${Number(params.value).toLocaleString()}` : "N/A",
    },
    {
      field: "debit",
      headerName: "Debit",
      flex: 0.5,
      renderCell: (params) =>
        params.value ? `NRs ${Number(params.value).toLocaleString()}` : "N/A",
    },
    {
      field: "balance",
      headerName: "Balance",
      flex: 0.5,
      renderCell: (params) => `NRs ${Number(params.value || 0).toLocaleString()}`,
    },
    {
      field: "createdAt",
      headerName: "createdAt Type",
      flex: 0.5,
      renderCell: (params) => {
        const date = new Date(params.value);
        return format(date, "yyyy-MM-dd");
      },
    },
    {
      field: "updatedAt",
      headerName: "updatedAt",
      flex: 0.5,
      renderCell: (params) => {
        const date = new Date(params.value);
        return format(date, "yyyy-MM-dd");
      },
    },
  ];

  const withdrawalColumns = [
    {
      field: "amount",
      headerName: "Amount",
      flex: 0.5,
      renderCell: (params) => `NRs ${Number(params.value).toLocaleString()}`,
    },
    {
      field: "payoutMethod",
      headerName: "Method",
      flex: 0.4,
      renderCell: (params) => (
        <Typography sx={{ textTransform: "capitalize", fontSize: "0.85rem" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.4,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          sx={{
            textTransform: "capitalize",
            backgroundColor: `${WITHDRAWAL_STATUS_COLORS[params.value] || "#7a7f9d"}22`,
            color: WITHDRAWAL_STATUS_COLORS[params.value] || "#7a7f9d",
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 0.7,
      renderCell: (params) => params.value || "—",
    },
    {
      field: "createdAt",
      headerName: "Requested",
      flex: 0.5,
      renderCell: (params) => format(new Date(params.value), "yyyy-MM-dd HH:mm"),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <ToastContainer />
      <Header title={`${userFullName}'s Statement`} />

      <Box
        mt="20px"
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }}
        gap="20px"
      >
        <StatBox
          title="Credit Points"
          value={walletBalance && walletBalance.creditPoints}
          caption="Available balance"
          loading={walletLoading}
          accent="#00c9a7"
          icon={<AccountBalanceWalletOutlined />}
        />
        <StatBox
          title="Reward Points"
          value={walletBalance && walletBalance.rewardPoints}
          caption="Available balance"
          loading={walletLoading}
          accent="#a370d1"
          icon={<CardGiftcardOutlined />}
        />
        <StatBox
          title="Locked Funds"
          value={walletBalance && walletBalance.lockedCreditPoints}
          caption="Pending withdrawal, not spendable"
          loading={walletLoading}
          accent="#de4437"
          icon={<LockOutlined />}
        />
      </Box>
      <Box display="flex" flexWrap="wrap" alignItems="flex-end">
        <FormControl variant="standard" sx={{ m: 2, minWidth: 200 }}>
          <InputLabel id="select-standard-label">Transfer Type</InputLabel>
          <Select
            id="select-standard"
            onChange={(e) => handleChange(e, "transType")}
            // onOpen={handleOpen}
            label="User"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="package">Package</MenuItem>
            <MenuItem value="load">Load</MenuItem>
            <MenuItem value="widthdraw">Widthdraw</MenuItem>
            {isDataFetched &&
              users.map((user) => (
                <MenuItem key={user.transType} value={user.transType}>
                  {user.transType}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {/* <FormControl variant="standard" sx={{ m: 2, minWidth: 200 }}>
          <div>
            <TextField
              id="from-date"
              label="From"
              type="date"
              value={dates.fromDate}
              onChange={(e) => handleChange(e, "fromDate")}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              id="to-date"
              label="To"
              type="date"
              value={dates.toDate}
              onChange={(e) => handleChange(e, "toDate")}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ marginLeft: 2 }}
            />
          </div>
        </FormControl> */}
        <FormControl variant="standard" sx={{ m: 2, minWidth: 100 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            onClick={handleSubmit}
          >
            Filter
          </Button>
        </FormControl>
      </Box>
      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.success,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          // loading={isLoading || !data}
          getRowId={(row) => row._id}
          rows={rowData}
          columns={columns}
          autoHeight
          disableColumnMenu
          disableColumnSelector
          hideFooter
          rowCount={(rowData && rowData.total) || 0}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          sortingMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSortModelChange={(newSortModel) => setSort(...newSortModel)}
          components={{
            Toolbar: DataGridCustomToolbar,
          }}
          componentsProps={{
            toolbar: {
              searchInput,
              setSearchInput,
              setSearch,
              columns,
              rowData,
            },
          }}
        />
      </Box>

      <Typography
        sx={{
          mt: "40px",
          mb: "12px",
          color: theme.palette.secondary[100],
          fontWeight: 700,
          fontSize: "0.95rem",
        }}
      >
        Withdrawal Requests
      </Typography>
      <Box
        height="50vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.background.alt,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
        }}
      >
        <DataGrid
          loading={withdrawalsLoading}
          getRowId={(row) => row._id}
          rows={withdrawalRequests}
          columns={withdrawalColumns}
          pagination
          pageSize={withdrawalPageSize}
          onPageSizeChange={(newPageSize) => setWithdrawalPageSize(newPageSize)}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Box>
    </Box>
  );
};
export default UserWalletStatement;
